import { RefObject, useEffect, useRef, useState } from 'react';
import { OrthographicCamera, PerspectiveCamera } from 'three';

import { isDefined } from '../../../utils/variables';

type CameraType = 'perspective' | 'orthographic';

/**
 * Setup default camera for ThreeJS.
 */
export function useCamera(ref: RefObject<HTMLElement>) {
  const [camera, setCamera] = useState<PerspectiveCamera | OrthographicCamera>();
  const [cameraType, setCameraType] = useState<CameraType>('orthographic');

  const orthographicCamera = useRef<OrthographicCamera>();
  const perspectiveCamera = useRef<PerspectiveCamera>();

  useEffect(() => {
    if (!isDefined(ref.current)) {
      return;
    }

    if (!isDefined(orthographicCamera.current)) {
      orthographicCamera.current = setupOrthographicCamera(ref.current);
    }
    if (!isDefined(perspectiveCamera.current)) {
      perspectiveCamera.current = setupPerspectiveCamera(ref.current);
    }

    const newCamera =
      cameraType === 'orthographic' ? orthographicCamera.current : perspectiveCamera.current;

    setCamera((oldValue) => {
      if (isDefined(oldValue)) {
        newCamera.position.copy(oldValue.position);
      }

      return newCamera;
    });
  }, [ref, cameraType]);

  return { camera, setCameraType };
}

function setupPerspectiveCamera(element: HTMLElement) {
  const perspectiveCamera = new PerspectiveCamera(
    75,
    element.clientWidth / element.clientHeight,
    0.1,
    10000,
  );
  perspectiveCamera.up.set(0, 0, 1);
  perspectiveCamera.updateMatrixWorld(true);
  return perspectiveCamera;
}

function setupOrthographicCamera(element: HTMLElement) {
  const width = element.clientWidth;
  const height = element.clientHeight;
  const aspectRatio = width / height;

  // Define the frustum boundaries.
  const frustumSize = 1000;
  const halfFrustumWidth = (frustumSize * aspectRatio) / 2;
  const halfFrustumHeight = frustumSize / 2;
  const left = -halfFrustumWidth;
  const right = halfFrustumWidth;
  const top = halfFrustumHeight;
  const bottom = -halfFrustumHeight;
  const near = 0.1;
  const far = 10000;

  const newCamera = new OrthographicCamera(left, right, top, bottom, near, far);

  newCamera.position.set(halfFrustumWidth, halfFrustumHeight, 5000);
  // Don't use lookAt for orthographic cameras - it modifies rotation
  // Instead, set quaternion directly to identity for top-down view
  newCamera.up.set(0, 1, 0);
  newCamera.quaternion.set(0, 0, 0, 1);

  return newCamera;
}
